let setupPromise: Promise<void> | null = null;

/**
 * Loads the AllowMe SDK script dynamically and waits for it to be ready.
 *
 * Resolution strategy (in order):
 *  1. `window.onSerasaExperianIDFReady` global callback (SDK v1/v2 pattern)
 *  2. `script.onload` + poll for `window.SerasaExperianIDF` (SDK v3 fallback)
 *  3. Safety timeout (30 s) to avoid hanging forever
 */
function loadScript(): Promise<void> {
  if (window.SerasaExperianIDF) {
    console.warn('[AllowMe] SDK already on window, skipping script injection');
    return Promise.resolve();
  }

  const url = import.meta.env.VITE_ALLOWME_SDK_URL as string | undefined;
  if (!url) {
    return Promise.reject(new Error('VITE_ALLOWME_SDK_URL is not configured'));
  }

  console.warn('[AllowMe] loading script:', url);

  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const settle = () => {
      if (settled) return;
      settled = true;
      console.warn('[AllowMe] script ready (SerasaExperianIDF available)');
      resolve();
    };

    // Strategy 1 — global callback (SDK v1/v2)
    window.onSerasaExperianIDFReady = () => {
      console.warn('[AllowMe] onSerasaExperianIDFReady fired');
      settle();
    };

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    script.type = 'text/javascript';

    // Strategy 2 — script.onload + poll (SDK v3 fallback)
    script.onload = () => {
      console.warn('[AllowMe] script onload fired');
      if (window.SerasaExperianIDF) {
        settle();
        return;
      }
      // Poll briefly — some SDKs initialise asynchronously after load
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.SerasaExperianIDF) {
          clearInterval(interval);
          settle();
        } else if (attempts >= 20) {
          clearInterval(interval);
          if (!settled) {
            settled = true;
            console.warn(
              '[AllowMe] SerasaExperianIDF not on window after polling'
            );
            reject(
              new Error(
                'SerasaExperianIDF not available on window after polling'
              )
            );
          }
        }
      }, 100);
    };

    script.onerror = () => {
      settled = true;
      reject(new Error('Failed to load AllowMe SDK script'));
    };

    document.head.appendChild(script);

    // Strategy 3 — safety timeout
    // setTimeout(() => {
    //   if (!settled) {
    //     settled = true;
    //     reject(
    //       new Error(
    //         `AllowMe SDK script did not become ready within ${SCRIPT_LOAD_TIMEOUT_MS}ms`
    //       )
    //     );
    //   }
    // }, SCRIPT_LOAD_TIMEOUT_MS);
  });
}

/**
 * Initialises the AllowMe device SDK.
 *
 * Loads the external script (if not already loaded), waits for
 * `onSerasaExperianIDFReady`, then calls `SerasaExperianIDF.setup()`.
 * Subsequent calls reuse the same promise so `setup` runs only once.
 */
export function initAllowMeSDK(): Promise<void> {
  if (setupPromise) {
    return setupPromise;
  }

  const apiKey = import.meta.env.VITE_ALLOWME_API_KEY as string | undefined;
  if (!apiKey) {
    setupPromise = Promise.reject(
      new Error('VITE_ALLOWME_API_KEY is not configured')
    );
    return setupPromise;
  }

  console.warn('[AllowMe] initialising SDK…');

  setupPromise = loadScript()
    .then(() => {
      if (!window.SerasaExperianIDF) {
        throw new Error('SerasaExperianIDF is not available on window');
      }
      console.warn('[AllowMe] calling setup()…');
      return window.SerasaExperianIDF.setup({ apiKey });
    })
    .then(() => {
      console.warn('[AllowMe] setup completed');
    });

  // Reset on failure so a retry is possible
  setupPromise.catch((err) => {
    console.warn('[AllowMe] setup() failed:', err.message);

    setupPromise = null;
  });

  return setupPromise;
}

/**
 * Collects device fingerprint data via the AllowMe SDK.
 *
 * Ensures the SDK is initialised first, then calls `collect()`.
 *
 * @param eventName - Optional event type identifier (defaults to `'get_collect'`).
 * @returns A base64-encoded encrypted fingerprint string.
 */
export async function collectDeviceData(eventName?: string): Promise<string> {
  await initAllowMeSDK();

  if (!window.SerasaExperianIDF) {
    throw new Error('SerasaExperianIDF is not available on window');
  }

  const options: AllowMeSDKCollectOptions = {};
  if (eventName) {
    options.metadata = { eventName };
  }

  console.warn('[AllowMe] calling collect()…');
  const data = await window.SerasaExperianIDF.collect(options);
  console.warn('[AllowMe] collect() returned data');
  return data;
}

/** Resets internal state — useful for tests. */
export function resetAllowMeSDK(): void {
  setupPromise = null;
}
